import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import { z } from "zod";

import {
  type ConfigureProductUomPayload,
  type CreateProductPayload,
  type PaginatedStockHistory,
  type Product,
  type StockAdjustment,
  type StockAdjustmentBatch,
  type StockAdjustmentBatchCreatePayload,
  type StockAdjustmentCreatePayload,
  type StockAdjustmentReversalPayload,
  type UnitOfMeasure,
  type Warehouse,
  paginatedStockHistorySchema,
  productSchema,
  productsSchema,
  stockAdjustmentBatchSchema,
  stockAdjustmentSchema,
  unitOfMeasuresSchema,
  warehousesSchema,
} from "./types/products";

const paginatedProductsSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: productsSchema,
  missing_base_uom_count: z.number().optional().default(0),
});

const inventoryCustomersSchema = z.array(
  z.object({
    id: z.coerce.string(),
    supplier: z.coerce.string(),
    erp_contact_id: z.coerce.string().optional().default(""),
    contact_name: z.string(),
    company_name: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.coerce.string().optional().default(""),
    phone: z.string(),
    mobile: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
  }),
);

const paginatedInventoryCustomersSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: inventoryCustomersSchema,
});

export type PaginatedProducts = z.infer<typeof paginatedProductsSchema>;
export type InventoryCustomers = z.infer<typeof inventoryCustomersSchema>;
export type PaginatedInventoryCustomers = z.infer<
  typeof paginatedInventoryCustomersSchema
>;
export type InventoryWarehouses = Warehouse[];
export type InventoryUnitOfMeasures = UnitOfMeasure[];

export class ProductService {
  static async list({
    page = 1,
    pageSize = 100,
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, string | undefined>;
  } = {}): Promise<PaginatedProducts | null> {
    try {
      const query: Record<string, string> = {
        page: page.toString(),
        page_size: pageSize.toString(),
      };

      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === "string" && value.trim()) {
          query[key] = value.trim();
        }
      }

      const response = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/products/",
          query,
        },
        z.union([paginatedProductsSchema, productsSchema]),
      );

      if (Array.isArray(response)) {
        const coerced: PaginatedProducts = {
          count: response.length,
          next: null,
          previous: null,
          results: response,
          missing_base_uom_count: 0,
        };
        return coerced;
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.list] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[ProductService.list] Unexpected error:", error);
      }
      return null;
    }
  }

  static async listCustomers({
    page = 1,
    pageSize = 100,
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, string | undefined>;
  } = {}): Promise<InventoryCustomers | []> {
    const pageData = await ProductService.listCustomersPage({
      page,
      pageSize,
      filters,
    });
    return pageData?.results ?? [];
  }

  static async listCustomersPage({
    page = 1,
    pageSize = 100,
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, string | undefined>;
  } = {}): Promise<PaginatedInventoryCustomers | null> {
    try {
      const query: Record<string, string> = {
        page: page.toString(),
        page_size: pageSize.toString(),
      };

      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === "string" && value.trim()) {
          query[key] = value.trim();
        }
      }

      const response = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/customers/",
          query,
        },
        z.union([paginatedInventoryCustomersSchema, inventoryCustomersSchema]),
      );

      if (Array.isArray(response)) {
        return {
          count: response.length,
          next: null,
          previous: null,
          results: response,
        };
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.listCustomersPage] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error(
          "[ProductService.listCustomersPage] Unexpected error:",
          error,
        );
      }
      return null;
    }
  }

  static async listWarehouses(): Promise<InventoryWarehouses> {
    try {
      return await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/warehouses/",
        },
        warehousesSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.listWarehouses] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error(
          "[ProductService.listWarehouses] Unexpected error:",
          error,
        );
      }
      return [];
    }
  }

  static async listUoms(): Promise<InventoryUnitOfMeasures> {
    try {
      return await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/uoms/",
        },
        unitOfMeasuresSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.listUoms] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[ProductService.listUoms] Unexpected error:", error);
      }
      return [];
    }
  }

  static async create(payload: CreateProductPayload): Promise<Product> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/products/",
        body: payload,
      },
      productSchema,
    );
  }

  static async delete(productId: number): Promise<void> {
    await __request(
      APIConfig,
      {
        method: "DELETE",
        url: `/api/v1/inventory/products/${productId}/`,
      },
      z.any(),
    );
  }

  static async retrieve(productId: number): Promise<Product> {
    return __request(
      APIConfig,
      {
        method: "GET",
        url: `/api/v1/inventory/products/${productId}/`,
      },
      productSchema,
    );
  }

  static async configureUom(
    productId: number,
    payload: ConfigureProductUomPayload,
  ): Promise<Product> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: `/api/v1/inventory/products/${productId}/configure-uom/`,
        body: payload,
      },
      productSchema,
    );
  }

  static async createStockAdjustment(
    payload: StockAdjustmentCreatePayload,
  ): Promise<StockAdjustment> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/stock-adjustments/",
        body: payload,
      },
      stockAdjustmentSchema,
    );
  }

  static async createStockAdjustmentBatch(
    payload: StockAdjustmentBatchCreatePayload,
  ): Promise<StockAdjustmentBatch> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/stock-adjustments/batch/",
        body: payload,
      },
      stockAdjustmentBatchSchema,
    );
  }

  static async listStockHistory(
    productId: number,
    filters: {
      warehouseId?: number;
      productBrandId?: number | "unallocated";
      source?: string;
      page?: number;
    } = {},
  ): Promise<PaginatedStockHistory> {
    const query: Record<string, string> = {
      page: String(filters.page ?? 1),
      page_size: "100",
    };
    if (filters.warehouseId) {
      query.warehouse_id = String(filters.warehouseId);
    }
    if (filters.productBrandId !== undefined) {
      query.product_brand_id = String(filters.productBrandId);
    }
    if (filters.source && filters.source !== "all") {
      query.source = filters.source;
    }
    return __request(
      APIConfig,
      {
        method: "GET",
        url: "/api/v1/inventory/stock-ledger-entries/",
        query: { ...query, product_id: String(productId) },
      },
      paginatedStockHistorySchema,
    );
  }

  static async createStockAdjustmentReversal(
    adjustmentId: number,
    payload: StockAdjustmentReversalPayload,
  ): Promise<StockAdjustment> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: `/api/v1/inventory/stock-adjustments/${adjustmentId}/reversals/`,
        body: payload,
      },
      stockAdjustmentSchema,
    );
  }

  static getStockHistoryExportUrl(
    productId: number,
    filters: {
      warehouseId?: number;
      productBrandId?: number | "unallocated";
      source?: string;
    } = {},
  ): string {
    const base = APIConfig.BASE?.replace(/\/$/, "") ?? "";
    const query = new URLSearchParams();
    if (filters.warehouseId) {
      query.set("warehouse_id", String(filters.warehouseId));
    }
    if (filters.productBrandId !== undefined) {
      query.set("product_brand_id", String(filters.productBrandId));
    }
    if (filters.source && filters.source !== "all") {
      query.set("source", filters.source);
    }
    query.set("product_id", String(productId));
    return `${base}/api/v1/inventory/stock-ledger-entries/export/?${query.toString()}`;
  }

  /**
   * Trigger a new product sync request.
   * Returns the created sync request or info about an existing one.
   */
  static async syncProducts(): Promise<SyncProductsResponse> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/sync-products/",
      },
      syncProductsResponseSchema,
    );
  }

  /**
   * Get sync status by ID or get the latest sync request.
   * @param id - Sync request ID or 'latest' to get the most recent
   */
  static async getSyncStatus(
    id: number | "latest" = "latest",
  ): Promise<ProductSyncRequest | null> {
    try {
      return await __request(
        APIConfig,
        {
          method: "GET",
          url: `/api/v1/inventory/sync-products/${id}/`,
        },
        productSyncRequestSchema,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }
}

// --- Sync Request Types ---

/**
 * Represents a product sync request record from the database.
 */
export const productSyncRequestSchema = z.object({
  id: z.number(),
  supplier: z.number(),
  triggered_by: z.number(),
  triggered_by_email: z.string(),
  status: z.enum(["pending", "processing", "completed", "failed"]),
  products_created: z.number().nullable(),
  products_updated: z.number().nullable(),
  products_total: z.number().nullable(),
  log: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProductSyncRequest = z.infer<typeof productSyncRequestSchema>;

/**
 * Response from triggering a new product sync.
 */
export const syncProductsResponseSchema = z.object({
  status: z.enum(["started", "already_running", "error"]),
  message: z.string(),
  sync_request: productSyncRequestSchema.optional(),
});

export type SyncProductsResponse = z.infer<typeof syncProductsResponseSchema>;
